import { defineStore } from "pinia";
import { ref, computed } from "vue";
import useMyFetch from "@stores/useMyFetch";
import handleError from "@utils/composables/handleError";
import { useCurrentUserStore } from "@stores/currentUser";
import { useSurveyStore } from "@stores/survey/survey";
import { captureEvent } from "@/lib/analytics";

export interface ReviewUser {
  id: number;
  full_name: string;
  initials: string;
}

export interface ReviewComment {
  id: number;
  created_at: string;
  edited_at: string | null;
  deleted: boolean;
  body: string | null;
  mention_ids: number[];
  posted_via: string | null;
  user: ReviewUser;
}

export interface ReviewThread {
  id: number;
  status: "open" | "resolved";
  resolved_at: string | null;
  created_at: string;
  card_token: string | null;
  created_by: ReviewUser;
  resolved_by: ReviewUser | null;
  review_comments: ReviewComment[];
}

export interface MentionableUser extends ReviewUser {
  kind: "client" | "csm";
}

type ReviewChannelMessage =
  | { action: "thread_created"; thread: ReviewThread }
  | { action: "comment_created"; thread_id: number; comment: ReviewComment }
  | { action: "comment_updated"; thread_id: number; comment: ReviewComment }
  | { action: "comment_deleted"; thread_id: number; comment_id: number }
  | { action: "thread_resolved"; thread_id: number; resolved_by: ReviewUser; resolved_at: string }
  | { action: "thread_reopened"; thread_id: number };

export const useReviewCommentsStore = defineStore("reviewComments", () => {
  const threads = ref<ReviewThread[]>([]);
  const mentionableUsers = ref<MentionableUser[]>([]);

  const panel = ref<{ open: boolean; contextCard: string | null; showAll: boolean }>({
    open: false,
    contextCard: null,
    showAll: true,
  });

  const currentUserStore = useCurrentUserStore();
  const surveyStore = useSurveyStore();

  const basePath = () => `surveys/${surveyStore.token}/review_threads`;
  const isMine = (comment: ReviewComment) => comment.user.id === currentUserStore.getUser?.id;

  const request = async (run: () => Promise<void>) => {
    try {
      await run();
    } catch (err) {
      handleError(err);
    }
  };

  const findThread = (threadId: number) => threads.value.find((thread) => thread.id === threadId);

  const prune = (thread: ReviewThread) => {
    const comments = thread.review_comments;
    while (comments.length && comments[comments.length - 1].deleted) comments.pop();
    if (!comments.some((comment) => !comment.deleted)) {
      threads.value = threads.value.filter((other) => other.id !== thread.id);
    }
  };

  const upsertThread = (incoming: ReviewThread) => {
    const existing = findThread(incoming.id);
    if (existing) Object.assign(existing, incoming);
    else threads.value.push(incoming);
    const thread = findThread(incoming.id);
    if (thread) prune(thread);
  };

  const upsertComment = (threadId: number, incoming: ReviewComment) => {
    const thread = findThread(threadId);
    if (!thread) return;
    const existing = thread.review_comments.find((comment) => comment.id === incoming.id);
    if (existing) Object.assign(existing, incoming);
    else thread.review_comments.push(incoming);
  };

  const applyCommentDeletion = (thread: ReviewThread, commentId: number) => {
    const index = thread.review_comments.findIndex((comment) => comment.id === commentId);
    if (index === -1) return;
    const comment = thread.review_comments[index];
    if (index < thread.review_comments.length - 1) {
      comment.deleted = true;
      comment.body = null;
      comment.mention_ids = [];
    } else {
      thread.review_comments.splice(index, 1);
    }
    prune(thread);
  };

  const markReopened = (thread: ReviewThread) => {
    thread.status = "open";
    thread.resolved_by = null;
    thread.resolved_at = null;
  };

  const fetchThreads = () =>
    request(async () => {
      const { response, data } = await useMyFetch(basePath()).json<{ review_threads: ReviewThread[] }>();
      if (response.value?.ok) {
        threads.value = data.value?.review_threads ?? [];
        threads.value.slice().forEach(prune);
      }
      const { response: usersResponse, data: usersData } = await useMyFetch(
        `surveys/${surveyStore.token}/review_mentionable_users`,
      ).json<{ users: MentionableUser[] }>();
      if (usersResponse.value?.ok) mentionableUsers.value = usersData.value?.users ?? [];
    });

  const threadsForCard = (cardToken: string) => threads.value.filter((thread) => thread.card_token === cardToken);
  const commentCountForCard = (cardToken: string) =>
    threadsForCard(cardToken)
      .filter((thread) => thread.status === "open")
      .reduce((count, thread) => count + thread.review_comments.filter((comment) => !comment.deleted).length, 0);
  const hasOpenComments = (cardToken: string) => commentCountForCard(cardToken) > 0;
  const totalOpenCount = computed(() => threads.value.filter((thread) => thread.status === "open").length);

  const openCardComments = (cardToken: string) => {
    panel.value = { open: true, contextCard: cardToken, showAll: false };
  };
  const openAllComments = () => {
    panel.value = { open: true, contextCard: null, showAll: true };
  };
  const setShowAll = (value: boolean) => {
    panel.value.showAll = value;
  };
  const closePanel = () => {
    panel.value.open = false;
  };

  const createThread = (cardToken: string | null, body: string, mentions: number[] = []) =>
    request(async () => {
      const { data, error } = await useMyFetch(basePath())
        .post({ review_thread: { body, card_token: cardToken, mention_ids: mentions } })
        .json<ReviewThread>();
      if (error.value) throw error.value;
      if (data.value) upsertThread(data.value);

      captureEvent("review_thread_created", {
        on_card: cardToken !== null,
        mentions_count: mentions.length,
      });
    });

  const postToCard = (cardToken: string, body: string, mentions: number[] = []) => createThread(cardToken, body, mentions);
  const postGeneral = (body: string, mentions: number[] = []) => createThread(null, body, mentions);

  const addComment = (thread: ReviewThread, body: string, mentions: number[] = []) =>
    request(async () => {
      const { data, error } = await useMyFetch(`${basePath()}/${thread.id}/review_comments`)
        .post({ review_comment: { body, mention_ids: mentions } })
        .json<ReviewComment>();
      if (error.value) throw error.value;
      if (!data.value) return;
      upsertComment(thread.id, data.value);
      markReopened(thread);

      captureEvent("review_comment_added", {
        thread_id: thread.id,
        mentions_count: mentions.length,
      });
    });

  const updateComment = (thread: ReviewThread, comment: ReviewComment, body: string, mentions: number[] = []) =>
    request(async () => {
      const { data, error } = await useMyFetch(`${basePath()}/${thread.id}/review_comments/${comment.id}`)
        .patch({ review_comment: { body, mention_ids: mentions } })
        .json<ReviewComment>();
      if (error.value) throw error.value;
      if (data.value) upsertComment(thread.id, data.value);

      captureEvent("review_comment_updated", { thread_id: thread.id });
    });

  const deleteComment = (thread: ReviewThread, comment: ReviewComment) =>
    request(async () => {
      const { response, error } = await useMyFetch(`${basePath()}/${thread.id}/review_comments/${comment.id}`).delete();
      if (error.value) throw error.value;
      if (response.value?.ok) {
        applyCommentDeletion(thread, comment.id);
        captureEvent("review_comment_deleted", { thread_id: thread.id });
      }
    });

  const updateThreadStatus = (thread: ReviewThread, status: "open" | "resolved") =>
    request(async () => {
      const { data, error } = await useMyFetch(`${basePath()}/${thread.id}`)
        .patch({ review_thread: { status } })
        .json<ReviewThread>();
      if (error.value) throw error.value;
      if (data.value) upsertThread(data.value);

      if (status === "resolved") {
        captureEvent("review_thread_resolved", { thread_id: thread.id });
      } else {
        captureEvent("review_thread_reopened", { thread_id: thread.id });
      }
    });

  const resolveThread = (thread: ReviewThread) => updateThreadStatus(thread, "resolved");
  const reopenThread = (thread: ReviewThread) => updateThreadStatus(thread, "open");

  const handleChannelMessage = (message: ReviewChannelMessage) => {
    switch (message.action) {
      case "thread_created":
        upsertThread(message.thread);
        break;
      case "comment_created":
      case "comment_updated":
        upsertComment(message.thread_id, message.comment);
        break;
      case "comment_deleted": {
        const thread = findThread(message.thread_id);
        if (thread) applyCommentDeletion(thread, message.comment_id);
        break;
      }
      case "thread_resolved": {
        const thread = findThread(message.thread_id);
        if (!thread) break;
        thread.status = "resolved";
        thread.resolved_by = message.resolved_by;
        thread.resolved_at = message.resolved_at;
        break;
      }
      case "thread_reopened": {
        const thread = findThread(message.thread_id);
        if (thread) markReopened(thread);
        break;
      }
    }
  };

  const reset = () => {
    threads.value = [];
    mentionableUsers.value = [];
    panel.value = { open: false, contextCard: null, showAll: true };
  };

  return {
    threads,
    panel,
    mentionableUsers,
    isMine,
    fetchThreads,
    threadsForCard,
    commentCountForCard,
    hasOpenComments,
    totalOpenCount,
    openCardComments,
    openAllComments,
    setShowAll,
    closePanel,
    addComment,
    updateComment,
    deleteComment,
    postToCard,
    postGeneral,
    resolveThread,
    reopenThread,
    handleChannelMessage,
    reset,
  };
});
